import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiMessageSquare, FiCalendar, FiAlertCircle, FiClipboard, FiChevronsRight, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { coachingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';

const PROBLEM_SUGGESTIONS = ['ปิดการขายไม่ได้', 'ลูกค้าปฏิเสธ', 'หมดไฟ', 'ไม่มีรายชื่อ'];
const ACTION_PLAN_SUGGESTIONS = ['ฝึกบทพูด', 'เพิ่มกิจกรรม', 'โทรหาลูกค้าเก่า', 'Role Play'];
const EXTRA_DETAIL_SUGGESTIONS = ['ติดตามผล 7 วัน', 'นัดคุยรอบหน้า', 'ให้กำลังใจ', '-'];
const TYPE_SUGGESTIONS = ['Face-to-Face', 'Phone', 'Online Meeting'];

const CoachingManager = ({ agentId, isEditable = true, showTitle = true }) => {
  const [coachingLogs, setCoachingLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form State
  const initialFormState = {
    logDate: new Date().toISOString().split('T')[0],
    topic: '',
    type: 'Face-to-Face',
    problemFound: '',
    actionPlan: '',
    extraDetail: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  // Delete Dialog State
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (agentId) {
      fetchCoachingLogs();
    }
  }, [agentId]);

  const fetchCoachingLogs = async () => {
    setLoading(true);
    try {
      // This will be created in api.js
      const { data } = await coachingAPI.getCoachingLogsByAgent(agentId);
      setCoachingLogs(data || []);
    } catch (error) {
      console.error('Error fetching coaching logs:', error);
      toast.error('ไม่สามารถดึงข้อมูลการโค้ชชิ่งได้');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      logDate: item.logDate ? item.logDate.split('T')[0] : '',
      topic: item.topic,
      type: item.type,
      problemFound: item.problemFound || '',
      actionPlan: item.actionPlan || '',
      extraDetail: item.extraDetail || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        agentId: parseInt(agentId)
      };

      if (editingItem) {
        await coachingAPI.updateCoachingLog(editingItem.id, dataToSubmit);
        toast.success('อัปเดตข้อมูลเรียบร้อย');
      } else {
        await coachingAPI.createCoachingLog(dataToSubmit);
        toast.success('เพิ่มข้อมูลเรียบร้อย');
      }
      setShowModal(false);
      fetchCoachingLogs();
    } catch (error) {
      console.error('Error saving coaching log:', error);
      toast.error('ไม่สามารถบันทึกข้อมูลได้');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await coachingAPI.deleteCoachingLog(deleteId);
      toast.success('ลบข้อมูลเรียบร้อย');
      fetchCoachingLogs();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting coaching log:', error);
      toast.error('ไม่สามารถลบข้อมูลได้');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading && coachingLogs.length === 0) {
    return <div className="py-4"><LoadingSpinner /></div>;
  }

  const displayedLogs = isExpanded ? coachingLogs : coachingLogs.slice(0, 3);

  return (
    <div className="space-y-4">
      {(showTitle || isEditable) && (
        <div className="flex justify-between items-center mb-4">
          {showTitle ? (
            <div className="font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiMessageSquare} className="mr-2 text-indigo-500" />
              บันทึกการโค้ชชิ่ง ({coachingLogs.length})
            </div>
          ) : (
            <div></div>
          )}
          
          {isEditable && (
            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
              เพิ่มบันทึก
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {coachingLogs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            ยังไม่มีบันทึกการโค้ชชิ่ง
          </p>
        ) : (
          displayedLogs.map((item) => (
            <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <h5 className="font-semibold text-gray-900 text-base">{item.topic}</h5>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{item.type}</span>
                  </div>
                  <p className="text-sm text-gray-500 flex items-center mb-2">
                    <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 mr-1.5" />
                    {formatDate(item.logDate)}
                  </p>
                  
                  {item.problemFound && (
                    <div className="text-sm text-red-700 mt-2 p-2 bg-red-50 rounded-md">
                      <p className="font-medium flex items-center"><SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1.5" />ปัญหาที่พบ:</p>
                      <p className="pl-5">{item.problemFound}</p>
                    </div>
                  )}

                  {item.actionPlan && (
                    <div className="text-sm text-green-700 mt-2 p-2 bg-green-50 rounded-md">
                      <p className="font-medium flex items-center"><SafeIcon icon={FiClipboard} className="w-4 h-4 mr-1.5" />แผนงาน:</p>
                      <p className="pl-5">{item.actionPlan}</p>
                    </div>
                  )}
                  
                  {item.extraDetail && (
                    <p className="text-sm text-gray-500 italic mt-2 border-l-2 border-gray-200 pl-2">
                      {item.extraDetail}
                    </p>
                  )}
                </div>

                {isEditable && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => openEditModal(item)}
                      className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                      title="แก้ไข"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteId(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="ลบ"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {coachingLogs.length > 3 && (
        <div className="text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center w-full"
          >
            {isExpanded ? 'แสดงน้อยลง' : 'แสดงทั้งหมด'}
            <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingItem ? 'แก้ไขบันทึก' : 'เพิ่มบันทึกการโค้ชชิ่ง'}
                    </h3>
                    <button type="button" onClick={() => setShowModal(false)} className="bg-white rounded-md text-gray-400 hover:text-gray-500">
                      <SafeIcon icon={FiX} className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">วันที่</label>
                            <input type="date" name="logDate" required value={formData.logDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ประเภท</label>
                            <select name="type" value={formData.type} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm">
                                <option>Face-to-Face</option>
                                <option>Phone</option>
                                <option>Online Meeting</option>
                            </select>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {TYPE_SUGGESTIONS.map(t => (
                                  <button
                                    key={t}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, type: t }))}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                                  >
                                    {t}
                                  </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ/เรื่องที่คุย <span className="text-red-500">*</span></label>
                      <input type="text" name="topic" required value={formData.topic} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ปัญหาที่พบ</label>
                      <textarea name="problemFound" rows={3} value={formData.problemFound} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"></textarea>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {PROBLEM_SUGGESTIONS.map(text => (
                          <button
                            key={text}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, problemFound: text }))}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">แผนงาน/สิ่งที่ต้องทำต่อ</label>
                      <textarea name="actionPlan" rows={3} value={formData.actionPlan} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"></textarea>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {ACTION_PLAN_SUGGESTIONS.map(text => (
                          <button
                            key={text}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, actionPlan: text }))}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">รายละเอียดเพิ่มเติม</label>
                      <textarea name="extraDetail" rows={2} value={formData.extraDetail} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"></textarea>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {EXTRA_DETAIL_SUGGESTIONS.map(text => (
                          <button
                            key={text}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, extraDetail: text }))}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            {text}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 font-medium text-white hover:bg-indigo-700 sm:ml-3 sm:w-auto sm:text-sm">
                    บันทึก
                  </button>
                  <button type="button" onClick={() => setShowModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                    ยกเลิก
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="ลบบันทึกการโค้ชชิ่ง"
        message="คุณแน่ใจหรือไม่ที่จะลบบันทึกนี้? การกระทำนี้ไม่สามารถเรียกคืนได้"
        confirmText="ลบ"
        cancelText="ยกเลิก"
        type="danger"
      />
    </div>
  );
};

export default CoachingManager;
